import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Tabs, TabsProps } from 'antd';
import './notification-container.scss';
import NotificationFilter from './notification-filter';
import CustomIcon, { IconType } from '../../shared/custom-icon/custom-icon';
import { NotificationDto } from '@src/dtos/notification.dto';
import { useStores } from '../../store';
import { observer } from 'mobx-react-lite';
import { EmptyState } from '../../shared/empty-state/empty-state';
import NotificationLoaderIcon from '@svg/notification-loader.svg?react';
import NotificationItem from './notification-item';
import InboxIcon from '@svg/functional-icon_inbox_32.svg?react';
import GiftIcon from '@svg/functional-icon_gift_32.svg?react';
import { WhatsNewList } from './whats-new/list/WhatsNewList';
import { WhatsNewDetailModal } from './whats-new/detail-modal/WhatsNewDetailModal';
import { WhatsNewEntry } from '@src/dtos/whats-new.dto';

type ActiveTab = 'inbox' | 'whats-new';

interface NotificationContainerProps {
    notifications: NotificationDto[];
    visible: boolean;
    close: () => void;
    fetchAllServices?: boolean;
}

/** Internal tab label with icon and unread dot */
const TabLabel: React.FC<{ label: string; icon: React.FC<React.SVGProps<SVGSVGElement>>; hasUnread: boolean }> = ({ label, icon: Icon, hasUnread }) => (
    <span className="tab-label">
        <span className="tab-label__icon-wrapper">
            <Icon className="tab-label__icon" aria-hidden="true" />
            {hasUnread && <span className="tab-label__dot" aria-hidden="true" />}
        </span>
        {label}
    </span>
);

const NotificationContainer: React.FC<NotificationContainerProps> = observer(({ visible, close, notifications, fetchAllServices }) => {

    const [filteredNotifications, setFilteredNotifications] = useState<NotificationDto[]>(notifications);
    const popupRef = useRef<HTMLDivElement | null>(null);
    const [whatsNewEntry, setWhatsNewEntry] = useState<WhatsNewEntry | null>(null);

    const { notificationStore, customerStore } = useStores();

    // Each time the panel opens, pick the right default tab based on current notification count.
    const [activeTab, setActiveTab] = useState<ActiveTab>('inbox');

    useEffect(() => {
        if (visible) {
            setActiveTab(notificationStore.newNotificationsCount > 0 ? 'inbox' : 'whats-new');
        }
    }, [visible]); 

    const handleClickOutside = useCallback((event: any) => {
        if (popupRef.current && !popupRef.current.contains(event.target) && visible) {
            handleClose();
        }
    }, [visible, close, activeTab]);

    useEffect(() => {
        document.addEventListener('click', handleClickOutside, true);
        return () => {
            document.removeEventListener('click', handleClickOutside, true);
        };
    }, [handleClickOutside]);

    const handleClose = () => {
        if (activeTab === 'whats-new') {
            void notificationStore.updateWhatsNewLastSeenDate();
        }
        close();
    };

    const handleTabChange = (key: string) => {
        setActiveTab(key as ActiveTab);
        if (key === 'whats-new') {
            void notificationStore.updateWhatsNewLastSeenDate();
        }
    };

    const refresh = () => {
        void notificationStore.loadNotifications(fetchAllServices);
    };

    const disabledClass = notificationStore.isLoading ? 'disabled' : '';

    const NotificationLoader = () => (
        <div className="notification-loader">
            <span className="notification-loader-icon">
                <NotificationLoaderIcon />
            </span>
            <span>Syncing Notifications...</span>
        </div>
    );

    const getServiceNameIfNeeded = (serviceId: number) => {
        if (fetchAllServices) {
            const service = customerStore.getCustomerServiceData(serviceId);
            return service?.serviceName || service?.adiCircuitId || undefined;
        }
        return undefined;
    };

    const tabItems: TabsProps['items'] = [
        {
            key: 'inbox',
            label: (
                <TabLabel
                    label="Inbox"
                    icon={InboxIcon}
                    hasUnread={notificationStore.newNotificationsCount > 0}
                />
            ),
            children: (
                <>
                    <NotificationFilter setData={setFilteredNotifications} data={notifications} />
                    {notificationStore.isLoading && <NotificationLoader />}
                    {!filteredNotifications.length && (
                        <EmptyState iconType={IconType.NOTIFICATION_EMPTY_STATE} title="No Notifications" />
                    )}
                    {!!filteredNotifications.length && (
                        <div className="notification-list">
                            {filteredNotifications.map(notification => (
                                <NotificationItem
                                    notification={notification}
                                    key={notification.id}
                                    serviceName={getServiceNameIfNeeded(notification.serviceId)}
                                    isNew={notificationStore.isNewNotification(notification)}
                                />
                            ))}
                        </div>
                    )}
                </>
            ),
        },
        {
            key: 'whats-new',
            label: (
                <TabLabel
                    label="Whats new"
                    icon={GiftIcon}
                    hasUnread={notificationStore.hasWhatsNewUnread}
                />
            ),
            children: <WhatsNewList onSeeWhatsNew={(entry) => { setWhatsNewEntry(entry); handleClose(); }} />,
        },
    ];

    return (
        <>
            {visible ? (
                <div className="notification-container" ref={popupRef}>
                    <Tabs
                        activeKey={activeTab}
                        onChange={handleTabChange}
                        items={tabItems}
                        className="notification-tabs"
                        tabBarExtraContent={{
                            right: (
                                <div className="notification-header-actions">
                                    <button type="button" className={`notification-refresh ${disabledClass}`} aria-label="Refresh" onClick={refresh}>
                                        <CustomIcon type={IconType.REFRESH} />
                                    </button>
                                    <button type="button" className="notification-close" aria-label="Close" onClick={handleClose}>
                                        <CustomIcon type={IconType.X} />
                                    </button>
                                </div>
                            ),
                        }}
                    />
                </div>
            ) : null}
            <WhatsNewDetailModal entry={whatsNewEntry} onClose={() => setWhatsNewEntry(null)} />
        </>
    );
});

export default NotificationContainer;
